// Small shared helpers.

export const uid = (p = "id"): string =>
  `${p}_${Math.random().toString(36).slice(2, 9)}${Date.now().toString(36).slice(-4)}`;

export const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));

/** decibel <-> linear gain */
export const dbToGain = (db: number): number => (db <= -60 ? 0 : Math.pow(10, db / 20));
export const gainToDb = (g: number): number => (g <= 0.0001 ? -Infinity : 20 * Math.log10(g));

export const fmtTime = (sec: number): string => {
  if (!isFinite(sec)) sec = 0;
  const sign = sec < 0 ? "-" : "";
  sec = Math.abs(sec);
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const ms = Math.floor((sec - Math.floor(sec)) * 1000);
  return `${sign}${m}:${s.toString().padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
};

/** Bars:beats from seconds given tempo + beats-per-bar. */
export const fmtBars = (sec: number, bpm: number, beatsPerBar = 4): string => {
  const beats = (sec * bpm) / 60;
  const bar = Math.floor(beats / beatsPerBar) + 1;
  const beat = Math.floor(beats % beatsPerBar) + 1;
  const tick = Math.floor(((beats % 1) * 480));
  return `${bar}.${beat}.${tick.toString().padStart(3, "0")}`;
};

export const secPerBeat = (bpm: number): number => 60 / bpm;

/** SMPTE-style timecode HH:MM:SS:FF from seconds at a given frame rate. */
export const fmtTimecode = (sec: number, fps: number): string => {
  const f = Math.max(1, Math.round(fps || 30));
  const total = Math.max(0, Math.floor((isFinite(sec) ? sec : 0) * f));
  const ff = total % f;
  const s = Math.floor(total / f) % 60;
  const m = Math.floor(total / (f * 60)) % 60;
  const h = Math.floor(total / (f * 3600));
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(h)}:${p(m)}:${p(s)}:${p(ff)}`;
};

/** Parse a typed time: "HH:MM:SS:FF", "MM:SS", "SS", or seconds like "12.5" / "12.5s".
 *  Returns null if it can't be read. */
export const parseTimecode = (text: string, fps: number): number | null => {
  const s = text.trim().replace(/s$/i, "");
  if (!s) return null;
  if (/^-?\d+(\.\d+)?$/.test(s)) return Math.max(0, Number(s));
  const parts = s.split(/[:;]/).map((p) => Number(p));
  if (parts.some((n) => !isFinite(n) || n < 0)) return null;
  const f = Math.max(1, Math.round(fps || 30));
  if (parts.length === 4) { const [h, m, sec, fr] = parts; return h * 3600 + m * 60 + sec + fr / f; }
  if (parts.length === 3) { const [h, m, sec] = parts; return h * 3600 + m * 60 + sec; }
  if (parts.length === 2) { const [m, sec] = parts; return m * 60 + sec; }
  return null;
};

/** Create an element with optional class/attrs/children. Tiny DOM helper. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: { class?: string; text?: string; html?: string; attrs?: Record<string, string>; on?: Partial<Record<keyof HTMLElementEventMap, (e: any) => void>> } = {},
  children: (Node | string)[] = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (opts.class) node.className = opts.class;
  if (opts.text != null) node.textContent = opts.text;
  if (opts.html != null) node.innerHTML = opts.html;
  if (opts.attrs) for (const [k, v] of Object.entries(opts.attrs)) node.setAttribute(k, v);
  if (opts.on) for (const [k, fn] of Object.entries(opts.on)) node.addEventListener(k, fn as any);
  for (const c of children) node.append(c as any);
  return node;
}

const PALETTE = ["#5b8cff", "#ff6b6b", "#22c55e", "#eab308", "#a855f7", "#06b6d4", "#f97316", "#ec4899"];
export const pickColor = (i: number): string => PALETTE[i % PALETTE.length];

export type Listener<T> = (payload: T) => void;
/** Minimal typed event emitter. */
export class Emitter<Events extends Record<string, any>> {
  private map = new Map<keyof Events, Set<Listener<any>>>();
  on<K extends keyof Events>(ev: K, fn: Listener<Events[K]>): () => void {
    let s = this.map.get(ev);
    if (!s) this.map.set(ev, (s = new Set()));
    s.add(fn);
    return () => s!.delete(fn);
  }
  emit<K extends keyof Events>(ev: K, payload: Events[K]): void {
    this.map.get(ev)?.forEach((fn) => fn(payload));
  }
}
